import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  IconButton,
  Box,
  Grid,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { FeeStructureDto, FeeInstallmentDto } from "../../hooks/useFeeStructure";
import { useSessionClass } from "../../contexts/SessionClassContext";
import { useTranslation } from "react-i18next";
import { useError } from "../../contexts/ErrorContext";
import { useAuth } from "../../contexts/AuthContext";
import { useClasses } from "../../hooks/useClasses";
import { useSessions } from "../../hooks/useSession";

// Local type for form state that allows string amounts for better input handling
interface FeeInstallmentFormData {
  installmentNumber: number;
  amount: number | string; // Allow string for empty/partial input
  dueDate: string | null;
}

interface FeeStructureModalProps {
  open: boolean;
  handleClose: () => void;
  initialData?: FeeStructureDto;
  onSubmit: (formData: FeeStructureDto) => void;
  isEdit?: boolean;
}

const FeeStructureModal: React.FC<FeeStructureModalProps> = ({
  open,
  handleClose,
  initialData,
  onSubmit,
  isEdit = false,
}) => {
  const { selectedSession: contextSession, selectedClass: contextClass } = useSessionClass();
  const { t } = useTranslation();
  const { showError } = useError();
  const { user, isTeacher } = useAuth();

  // Get schoolId
  const schoolId = useMemo(() => {
    if (isTeacher()) {
      return user?.schoolId;
    }
    return user?.schoolId;
  }, [user?.schoolId, isTeacher]);

  // Fetch classes and sessions (convert null to undefined for hooks)
  const { classesQuery } = useClasses(schoolId ?? undefined);
  const { sessionsQuery } = useSessions(schoolId ?? undefined);

  // Helper to derive base class name (without section)
  const getBaseClassName = useCallback((name?: string) => {
    if (!name) return "";
    return name.split(" ")[0];
  }, []);

  // Unique classes by base name (first occurrence wins)
  const uniqueClasses = useMemo(() => {
    const map = new Map<string, any>();
    (classesQuery.data || []).forEach((cls) => {
      const base = getBaseClassName(cls.name);
      if (base && !map.has(base)) {
        map.set(base, cls);
      }
    });
    return Array.from(map.values());
  }, [classesQuery.data, getBaseClassName]);

  // Local state for selected session and class in modal
  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(null);
  const [selectedClassId, setSelectedClassId] = useState<number | null>(null);

  const [feeType, setFeeType] = useState<string>("TUITION_FEES");
  const [feeInstallments, setFeeInstallments] = useState<FeeInstallmentFormData[]>([
    {
      installmentNumber: 1,
      amount: "", // Start with empty string instead of 0
      dueDate: null,
    },
  ]);

  useEffect(() => {
    if (open) {
      if (isEdit && initialData) {
        setFeeType(initialData.feeType || "TUITION_FEES");
        setSelectedSessionId(initialData.sessionId || null);
        setSelectedClassId(initialData.classId || null);
        setFeeInstallments(
          initialData.feeInstallments && initialData.feeInstallments.length > 0
            ? initialData.feeInstallments.map(inst => {
              // Handle dueDate - can be array format [year, month, day, hour, minute, second, nanoseconds]
              let dueDateValue: string | null = (inst.dueDate as any) ?? null;

              if (dueDateValue) {
                try {
                  let date: Date;

                  // Check if it's an array format (Java LocalDateTime)
                  if (Array.isArray(dueDateValue)) {
                    // Array format: [year, month, day, hour, minute, second?, nanoseconds?]
                    // Note: month in array is 1-indexed, but JavaScript Date uses 0-indexed months
                    const [year, month, day, hour = 0, minute = 0, second = 0] = dueDateValue;
                    date = new Date(year, month - 1, day, hour, minute, second);
                  } else if (typeof dueDateValue === 'string') {
                    date = new Date(dueDateValue);
                  } else if (typeof dueDateValue === 'number') {
                    date = new Date(dueDateValue);
                  } else {
                    date = new Date(dueDateValue as any);
                  }

                  dueDateValue = !isNaN(date.getTime()) ? date.toISOString() : null;
                } catch (error) {
                  dueDateValue = null;
                }
              } else {
                dueDateValue = null;
              }

              return {
                installmentNumber: inst.installmentNumber,
                amount: inst.amount === 0 ? "" : inst.amount,
                dueDate: dueDateValue
              };
            })
            : [
              {
                installmentNumber: 1,
                amount: "",
                dueDate: null,
              },
            ]
        );
      } else {
        setFeeType("TUITION_FEES");
        // Initialize with context values if available, otherwise null
        setSelectedSessionId(contextSession?.id || null);
        setSelectedClassId(contextClass?.id || null);
        setFeeInstallments([
          {
            installmentNumber: 1,
            amount: "",
            dueDate: null,
          },
        ]);
      }
    }
  }, [open, initialData, isEdit, contextSession?.id, contextClass?.id]);

  // Set default class when session is selected and classes are loaded
  useEffect(() => {
    if (open && selectedSessionId && classesQuery.data && !selectedClassId) {
      // Filter classes for the selected session
      const availableClasses = classesQuery.data.filter((cls) => {
        const classSessionId = cls.sessionId;
        if (classSessionId === null || classSessionId === undefined) {
          return true; // Include classes without sessionId
        }
        return Number(classSessionId) === Number(selectedSessionId);
      });

      // Set the first available class as default
      if (availableClasses.length > 0) {
        setSelectedClassId(availableClasses[0].id || null);
      }
    }
  }, [open, selectedSessionId, classesQuery.data, selectedClassId]);

  const handleAddInstallment = () => {
    const nextNumber = feeInstallments.length + 1;
    setFeeInstallments([
      ...feeInstallments,
      {
        installmentNumber: nextNumber,
        amount: "",
        dueDate: null,
      },
    ]);
  };

  const handleRemoveInstallment = (index: number) => {
    if (feeInstallments.length > 1) {
      const updated = feeInstallments.filter((_, i) => i !== index);
      // Renumber installments
      const renumbered = updated.map((inst, idx) => ({
        ...inst,
        installmentNumber: idx + 1,
      }));
      setFeeInstallments(renumbered);
    }
  };

  const handleInstallmentChange = (
    index: number,
    field: keyof FeeInstallmentFormData,
    value: any
  ) => {
    const updated = [...feeInstallments];
    // If changing amount, handle empty string and prevent leading zeros
    if (field === "amount") {
      // If value is empty string, keep it as empty
      if (value === "") {
        updated[index] = {
          ...updated[index],
          [field]: "",
        };
      } else {
        // Only allow whole numbers - reject decimal input
        // Check if value contains a decimal point
        if (value.toString().includes(".")) {
          // Remove decimal point and everything after it
          const intValue = parseInt(value.toString().split(".")[0], 10);
          if (!isNaN(intValue) && intValue >= 0) {
            updated[index] = {
              ...updated[index],
              [field]: intValue.toString(),
            };
          } else {
            // If invalid, keep previous value
            return;
          }
        } else {
          // Remove leading zeros and convert to integer, then back to string for display
          // This prevents "03483" from appearing - it will be "3483"
          const intValue = parseInt(value, 10);
          if (!isNaN(intValue) && intValue >= 0) {
            // Store as string for display, but remove leading zeros
            updated[index] = {
              ...updated[index],
              [field]: intValue.toString(),
            };
          } else {
            // If not a valid number, keep the string as is (for partial input)
            updated[index] = {
              ...updated[index],
              [field]: value,
            };
          }
        }
      }
    } else {
      updated[index] = {
        ...updated[index],
        [field]: value,
      };
    }
    setFeeInstallments(updated);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedSessionId) {
      showError(t("please_select_a_session") || "Please select a session");
      return;
    }

    if (!selectedClassId) {
      showError(t("please_select_a_class") || "Please select a class");
      return;
    }

    // Validate installments
    if (feeInstallments.length === 0) {
      showError(t("please_add_at_least_one_fee_installment") || "Please add at least one fee installment");
      return;
    }

    for (let i = 0; i < feeInstallments.length; i++) {
      const inst = feeInstallments[i];
      const amountValue = typeof inst.amount === "string" ? parseInt(inst.amount, 10) : Math.floor(inst.amount);
      if (!amountValue || amountValue <= 0 || isNaN(amountValue)) {
        showError(`${t("installment") || "Installment"} ${i + 1}: ${t("installment_amount_must_be_greater_than_zero") || "Amount must be greater than 0"}`);
        return;
      }
      if (!inst.dueDate) {
        showError(`${t("installment") || "Installment"} ${i + 1}: ${t("installment_due_date_is_required") || "Due date is required"}`);
        return;
      }
    }

    // Validate installment dates are strictly increasing
    const toDate = (value: string | Date | null | undefined): Date | null => {
      if (!value) return null;
      try {
        if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
        const d = new Date(value);
        return isNaN(d.getTime()) ? null : d;
      } catch {
        return null;
      }
    };

    for (let i = 1; i < feeInstallments.length; i++) {
      const prev = toDate(feeInstallments[i - 1].dueDate as any);
      const curr = toDate(feeInstallments[i].dueDate as any);
      if (!prev || !curr) {
        showError(`${t("installment") || "Installment"} ${i + 1}: ${t("installment_due_date_is_required") || "Due date is required"}`);
        return;
      }
      if (curr.getTime() <= prev.getTime()) {
        showError(`${t("installment") || "Installment"} ${i + 1}: ${t("due_date_must_be_after_previous") || "Due date must be after previous installment"}`);
        return;
      }
    }

    // Get the selected class to extract className
    const selectedClass = classesQuery.data?.find((cls) => cls.id === selectedClassId);
    const className = selectedClass ? getBaseClassName(selectedClass.name) : "";

    const formData: FeeStructureDto = {
      sessionId: selectedSessionId,
      classId: selectedClassId,
      feeType: feeType,
      totalAnnualFees: 0.0,
      totalInstallments: 0,
      className: className,
      feeInstallments: feeInstallments.map((inst): FeeInstallmentDto => ({
        installmentNumber: inst.installmentNumber,
        amount: typeof inst.amount === "string" ? (parseInt(inst.amount, 10) || 0) : Math.floor(inst.amount),
        dueDate: inst.dueDate || "",
      })),
    };

    onSubmit(formData);
  };

  const isFormValid =
    selectedSessionId !== null &&
    selectedClassId !== null &&
    feeInstallments.length > 0 &&
    feeInstallments.every((inst) => {
      const amountValue = typeof inst.amount === "string" ? parseInt(inst.amount, 10) : Math.floor(inst.amount);
      return amountValue > 0 && inst.dueDate;
    });

  // Filter classes by selected session
  const filteredClasses = useMemo(() => {
    if (!classesQuery.data) return [];

    // If no session selected, show all classes
    if (!selectedSessionId) {
      return classesQuery.data;
    }

    // Filter classes by sessionId
    return classesQuery.data.filter((cls) => {
      const classSessionId = cls.sessionId;

      // If class has no sessionId, include it (for backward compatibility)
      // This handles cases where classes might not have sessionId set
      if (classSessionId === null || classSessionId === undefined) {
        return true;
      }

      // Compare normalized IDs (handle number/string conversion)
      return Number(classSessionId) === Number(selectedSessionId);
    });
  }, [classesQuery.data, selectedSessionId]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
            handleClose();
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#f8f9fb",
            borderBottom: "2px solid #e0e7ff",
          }}
        >
          <Typography variant="h5" fontWeight="bold">
            {isEdit ? t("edit_fee_structure") || "Edit Fee Structure" : t("add_fee_structure") || "Add Fee Structure"}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              {isEdit ? (
                <TextField
                  fullWidth
                  label="Session"
                  value={
                    sessionsQuery.data?.find((s) => s.id === selectedSessionId)?.name || "-"
                  }
                  InputProps={{
                    readOnly: true,
                  }}
                  variant="outlined"
                />
              ) : (
                <FormControl fullWidth>
                  <InputLabel>Session *</InputLabel>
                  <Select
                    value={selectedSessionId || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSelectedSessionId(value === "" ? null : Number(value));
                      // Reset class when session changes
                      if (value === "") {
                        setSelectedClassId(null);
                      } else {
                        setSelectedClassId(null);
                      }
                    }}
                    label="Session *"
                    required
                  >
                    <MenuItem value="">
                      <em>{t("select_session") || "Select Session"}</em>
                    </MenuItem>
                    {sessionsQuery.data?.map((session) => (
                      <MenuItem key={session.id} value={session.id}>
                        {session.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              {isEdit ? (
                <TextField
                  fullWidth
                  label="Class"
                  value={
                    (() => {
                      const selectedClass = classesQuery.data?.find((cls) => cls.id === selectedClassId);
                      if (!selectedClass) return "-";
                      return getBaseClassName(selectedClass.name) || selectedClass.name;
                    })()
                  }
                  InputProps={{
                    readOnly: true,
                  }}
                  variant="outlined"
                />
              ) : (
                <FormControl fullWidth>
                  <InputLabel>Class *</InputLabel>
                  <Select
                    value={selectedClassId || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSelectedClassId(value === "" ? null : Number(value));
                    }}
                    label="Class *"
                    displayEmpty
                    disabled={filteredClasses.length === 0}
                    required
                  >
                    <MenuItem value="" disabled>
                      <em>{t("select_class") || "Select Class"}</em>
                    </MenuItem>
                    {uniqueClasses.map((cls) => (
                      <MenuItem key={cls.id} value={cls.id}>
                        {getBaseClassName(cls.name) || cls.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Fee Type</InputLabel>
                <Select
                  value={feeType}
                  onChange={(e) => setFeeType(e.target.value)}
                  label="Fee Type"
                >
                  <MenuItem value="TUITION_FEES">Tuition Fees</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6" fontWeight="bold">
                  Fee Installments
                </Typography>
                <Button
                  startIcon={<AddIcon />}
                  onClick={handleAddInstallment}
                  variant="outlined"
                  size="small"
                >
                  Add Installment
                </Button>
              </Box>
            </Grid>

            {feeInstallments.map((installment, index) => (
              <Grid item xs={12} key={index}>
                <Box
                  sx={{
                    p: 2,
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    backgroundColor: "#fafafa",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight="bold">
                      Installment {installment.installmentNumber}
                    </Typography>
                    {feeInstallments.length > 1 && (
                      <IconButton
                        onClick={() => handleRemoveInstallment(index)}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Installment Number"
                        type="number"
                        value={installment.installmentNumber}
                        disabled
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box
                        onWheel={(e) => {
                          // Prevent mouse wheel from changing the value
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        sx={{ width: '100%' }}
                      >
                        <TextField
                          fullWidth
                          label="Amount"
                          type="number"
                          value={typeof installment.amount === "string" ? installment.amount : (installment.amount === 0 ? "" : installment.amount)}
                          onChange={(e) => {
                            const inputValue = e.target.value;
                            // If input is empty, set to empty string
                            // Otherwise, pass the raw value to handle conversion
                            handleInstallmentChange(
                              index,
                              "amount",
                              inputValue === "" ? "" : inputValue
                            );
                          }}
                          onWheel={(e) => {
                            // Prevent mouse wheel from changing the value
                            e.preventDefault();
                            e.stopPropagation();
                            (e.target as HTMLInputElement).blur();
                          }}
                          onFocus={(e) => {
                            // Add wheel event listener when input is focused
                            const input = e.target as HTMLInputElement;
                            const handleWheel = (wheelEvent: WheelEvent) => {
                              wheelEvent.preventDefault();
                              wheelEvent.stopPropagation();
                              input.blur();
                            };
                            input.addEventListener('wheel', handleWheel, { passive: false });
                            // Cleanup on blur
                            const handleBlur = () => {
                              input.removeEventListener('wheel', handleWheel);
                              input.removeEventListener('blur', handleBlur);
                            };
                            input.addEventListener('blur', handleBlur);
                          }}
                          inputProps={{
                            min: 0,
                            step: 1,
                            style: {
                              MozAppearance: 'textfield'
                            }
                          }}
                          sx={{
                            '& input[type=number]': {
                              MozAppearance: 'textfield'
                            },
                            '& input[type=number]::-webkit-outer-spin-button': {
                              WebkitAppearance: 'none',
                              margin: 0
                            },
                            '& input[type=number]::-webkit-inner-spin-button': {
                              WebkitAppearance: 'none',
                              margin: 0
                            }
                          }}
                          required
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <DatePicker
                        label="Due Date"
                        value={
                          installment.dueDate
                            ? (() => {
                              try {
                                const date = new Date(installment.dueDate);
                                return isNaN(date.getTime()) ? null : date;
                              } catch (error) {
                                return null;
                              }
                            })()
                            : null
                        }
                        onChange={(newValue) => {
                          if (newValue) {
                            // Format date in local date-time format to preserve the exact date user selected
                            const year = newValue.getFullYear();
                            const month = String(newValue.getMonth() + 1).padStart(2, '0');
                            const day = String(newValue.getDate()).padStart(2, '0');
                            const hours = String(newValue.getHours()).padStart(2, '0');
                            const minutes = String(newValue.getMinutes()).padStart(2, '0');
                            const seconds = String(newValue.getSeconds()).padStart(2, '0');
                            const localDateTime = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
                            handleInstallmentChange(
                              index,
                              "dueDate",
                              localDateTime
                            );
                          }
                        }}
                        minDate={
                          index > 0
                            ? (() => {
                              const prev = feeInstallments[index - 1].dueDate;
                              if (!prev) return undefined;
                              const d = new Date(prev as any);
                              return isNaN(d.getTime()) ? undefined : d;
                            })()
                            : undefined
                        }
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            required: true,
                            onWheel: (e: React.WheelEvent<HTMLInputElement>) => {
                              // Prevent mouse wheel from changing the date value
                              e.preventDefault();
                              e.stopPropagation();
                              e.currentTarget.blur();
                            },
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            ))}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: "1px solid #e0e0e0" }}>
          <Button onClick={handleClose} color="inherit">
            {t("cancel") || "Cancel"}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!isFormValid}
            sx={{
              backgroundColor: "#3092e3",
              "&:hover": { backgroundColor: "primary.main" },
            }}
          >
            {isEdit ? t("update") || "Update" : t("add") || "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default FeeStructureModal;

